import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { parseNav, parseSchedule } from "@/components/AskAssistant";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Sparkles, Send, Loader2, ArrowRight, HelpCircle, LineChart, LifeBuoy, Mic, MicOff } from "lucide-react";

const SR = typeof window !== "undefined" ? window.SpeechRecognition || window.webkitSpeechRecognition : null;
const IS_IOS = typeof navigator !== "undefined" && /iPad|iPhone|iPod/.test(navigator.userAgent || "");
const HAS_MR = typeof window !== "undefined" && !!window.MediaRecorder && !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
const KIND_LABEL = { os: "OS", budget: "orçamento", plate: "placa", customer: "ficha do cliente" };

const HELP_SUGGEST = [
  "Como lanço uma peça na OS?",
  "Como cadastro o PIN do mecânico?",
  "Como uso a calculadora de mão de obra?",
  "Como abro um chamado de suporte?",
];
const DATA_SUGGEST = [
  "Quanto faturei em março?",
  "Compare este mês com o mês passado",
  "O que já foi feito na placa ABC1234?",
];

function Md({ text }) {
  const lines = (text || "").split("\n").filter((l) => l != null);
  const inline = (s) => s.split(/(\*\*[^*]+\*\*)/g).map((p, i) => (p.startsWith("**") ? <b key={i} className="text-zinc-50">{p.slice(2, -2)}</b> : p));
  return (
    <div>
      {lines.map((l, i) => {
        const t = l.trim();
        if (!t) return <div key={i} className="h-1" />;
        return <p key={i} className="my-1">{inline(t.replace(/^[-*]\s+/, "• "))}</p>;
      })}
    </div>
  );
}

export function AssistantFab() {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("help");
  const [q, setQ] = useState("");
  const [msgs, setMsgs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [usage, setUsage] = useState(null);
  const navigate = useNavigate();
  const [listening, setListening] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const recRef = useRef(null);
  const mrRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  useEffect(() => { if (open) { api.get("/ai/assistant/usage").then(({ data }) => setUsage(data)).catch(() => {}); } }, [open, mode, msgs.length]);

  const ask = async (text) => {
    const t = (text ?? q).trim();
    if (!t || busy) { return; }
    setQ(""); setMsgs((m) => [...m, { me: true, text: t }]); setBusy(true);
    try {
      if (await runCommand(t)) { return; }
      const endpoint = mode === "help" ? "/ai/help" : "/ai/assistant";
      const { data } = await api.post(endpoint, { question: t });
      setMsgs((m) => [...m, {
        text: data.answer, goto: data.goto, period: data.period,
        found: data.found, support_prefill: data.support_prefill,
      }]);
      if (data.used != null) setUsage({ used: data.used, limit: data.limit });
    } catch (e) {
      setMsgs((m) => [...m, { text: formatApiError(e), err: true }]);
    } finally { setBusy(false); }
  };

  const go = (it) => { setOpen(false); if (it.prefill) { navigate("/owner/agendamentos", { state: { prefill: it.prefill } }); return; } navigate(it.path); };

  const runCommand = async (t) => {
    const sch = parseSchedule(t);
    if (sch) {
      if (!sch.plate && !sch.name) { setMsgs((m) => [...m, { text: "Para agendar, diga o nome do cliente ou a placa. Ex.: “agenda a placa ABC1D23 amanhã às 14h troca de óleo”." }]); return true; }
      const { data } = await api.get("/ai/assistant/find", { params: { kind: "schedule", ...(sch.name ? { name: sch.name } : {}), ...(sch.plate ? { plate: sch.plate } : {}) } });
      const items = (data.items || []).map((it) => ({ ...it, prefill: { customer: { id: it.customer_id, name: it.customer_name }, plate: it.plate, vehicle_label: it.vehicle_label, date: sch.date || "", time: sch.time || "", service_name: sch.service || "" } }));
      const who = sch.plate || sch.name;
      if (items.length === 1) { setMsgs((m) => [...m, { text: `Abrindo novo agendamento para **${items[0].label}**. Confira e clique em Salvar.` }]); setTimeout(() => go(items[0]), 500); }
      else { setMsgs((m) => [...m, { text: items.length ? `Encontrei ${items.length} opções para "${who}". Escolha para agendar:` : `Não encontrei cliente ou veículo para "${who}".`, list: items }]); }
      return true;
    }
    const nav = parseNav(t);
    if (!nav) { return false; }
    const { data } = await api.get("/ai/assistant/find", { params: { kind: nav.kind, ...(nav.name ? { name: nav.name } : {}), ...(nav.plate ? { plate: nav.plate } : {}) } });
    const items = data.items || [];
    const who = nav.plate || nav.name;
    if (items.length === 1) { setMsgs((m) => [...m, { text: `Abrindo ${KIND_LABEL[nav.kind]}: **${items[0].label}** — ${items[0].sub}` }]); setTimeout(() => go(items[0]), 600); }
    else { setMsgs((m) => [...m, { text: items.length ? `Encontrei ${items.length} resultado(s) para "${who}". Escolha:` : `Não encontrei ${KIND_LABEL[nav.kind]} para "${who}".`, list: items }]); }
    return true;
  };

  const stopMediaStream = () => { try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* noop */ } streamRef.current = null; };

  const startWebSpeech = () => {
    try {
      const rec = new SR();
      rec.lang = "pt-BR"; rec.interimResults = true; rec.maxAlternatives = 1; rec.continuous = false;
      let final = "";
      rec.onresult = (e) => {
        let interim = "";
        for (let i = e.resultIndex; i < e.results.length; i += 1) {
          const r = e.results[i]; if (r.isFinal) { final += r[0].transcript; } else { interim += r[0].transcript; }
        }
        setQ(((final + " " + interim).trim()));
      };
      rec.onerror = (ev) => { if (ev.error && ev.error !== "no-speech" && ev.error !== "aborted") { toast.error(`Microfone: ${ev.error}`); } };
      rec.onend = () => setListening(false);
      recRef.current = rec; setListening(true); rec.start();
    } catch (err) { setListening(false); toast.error(`Não consegui iniciar o microfone: ${err?.message || err}`); }
  };

  const startMediaRecorder = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimes = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/aac", "audio/ogg"];
      const mime = mimes.find((m) => window.MediaRecorder.isTypeSupported?.(m)) || "";
      const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        setListening(false); stopMediaStream();
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        if (!blob.size) { return; }
        setTranscribing(true);
        try {
          const ext = (mr.mimeType || "").includes("mp4") ? "m4a" : (mr.mimeType || "").includes("ogg") ? "ogg" : "webm";
          const fd = new FormData(); fd.append("file", blob, `assistente.${ext}`);
          const { data } = await api.post("/ai/stt", fd, { headers: { "Content-Type": "multipart/form-data" } });
          const text = (data?.text || "").trim();
          if (text) { setQ(text); } else { toast.error("Não entendi o áudio. Fale de novo mais perto."); }
        } catch (err) { toast.error(`Não consegui transcrever: ${formatApiError(err)}`); }
        finally { setTranscribing(false); }
      };
      mrRef.current = mr; setListening(true); mr.start();
    } catch (err) { setListening(false); stopMediaStream(); toast.error(`Não consegui acessar o microfone: ${err?.message || err}`); }
  };

  const toggleMic = () => {
    if (listening) { if (mrRef.current && mrRef.current.state === "recording") { mrRef.current.stop(); return; } recRef.current?.stop(); return; }
    if (IS_IOS || !SR) {
      if (HAS_MR) { startMediaRecorder(); return; }
      toast.error("Seu navegador não aceita comando de voz. Use o Chrome no computador."); return;
    }
    startWebSpeech();
  };

  const openTicket = (prefill) => {
    const p = prefill || {};
    const params = new URLSearchParams();
    if (p.subject) params.set("subject", p.subject);
    if (p.description) params.set("description", p.description);
    setOpen(false);
    navigate(`/owner/suporte?${params.toString()}`);
  };

  const suggestions = mode === "help" ? HELP_SUGGEST : DATA_SUGGEST;
  const nearLimit = usage && usage.limit > 0 && usage.used / usage.limit >= 0.8 && usage.used < usage.limit;
  const overLimit = usage && usage.limit > 0 && usage.used >= usage.limit;

  return (
    <>
      <button type="button" data-testid="assistant-fab" aria-label="Ajuda do sistema" onClick={() => setOpen(true)}
        className="fixed z-40 bottom-[calc(5rem+env(safe-area-inset-bottom))] md:bottom-6 right-4 md:right-6 h-14 w-14 rounded-full border border-amber-500/60 bg-amber-500/90 text-black shadow-xl shadow-amber-500/20 hover:bg-amber-400 transition-colors flex items-center justify-center">
        <Sparkles className="h-6 w-6" />
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="assistant-dialog" className="bg-zinc-950 border border-zinc-800 text-zinc-100 max-w-lg max-h-[90dvh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-display uppercase flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-amber-300" /> Assistente da oficina
            </DialogTitle>
          </DialogHeader>

          <div data-testid="assistant-mode" className="grid grid-cols-2 gap-1 bg-zinc-900 rounded-lg p-1 text-xs font-bold uppercase">
            <button type="button" data-testid="assistant-mode-help" onClick={() => { setMode("help"); setMsgs([]); }}
                    className={`h-8 rounded-md flex items-center justify-center gap-1.5 ${mode === "help" ? "bg-amber-500 text-black" : "text-zinc-400"}`}>
              <HelpCircle className="h-3.5 w-3.5" /> Como faço
            </button>
            <button type="button" data-testid="assistant-mode-data" onClick={() => { setMode("data"); setMsgs([]); }}
                    className={`h-8 rounded-md flex items-center justify-center gap-1.5 ${mode === "data" ? "bg-amber-500 text-black" : "text-zinc-400"}`}>
              <LineChart className="h-3.5 w-3.5" /> Minha oficina
            </button>
          </div>

          <div className="flex-1 overflow-y-auto space-y-2 min-h-[12rem] text-sm">
            {!msgs.length && (
              <div className="space-y-2">
                <p className="text-zinc-400">
                  {mode === "help"
                    ? "Pergunte como fazer alguma coisa no AutoOficina. Mostro em qual menu e botão clicar."
                    : "Pergunte sobre faturamento, estoque, mecânicos, clientes… Respondo com os números da sua oficina."}
                </p>
                <div className="flex flex-wrap gap-2">
                  {suggestions.map((s) => (
                    <button key={s} type="button" data-testid={`fab-sg-${s.slice(0, 12)}`} onClick={() => ask(s)}
                            className="text-xs rounded-full border border-zinc-700 px-3 py-1.5 text-zinc-300 hover:bg-zinc-900">{s}</button>
                  ))}
                </div>
              </div>
            )}
            {msgs.map((m, i) => (
              <div key={i} data-testid={m.me ? `assistant-q-${i}` : `assistant-a-${i}`}
                   className={`rounded-xl px-3 py-2 ${m.me ? "ml-10 bg-green-brand/15 text-green-100" : m.err ? "mr-10 bg-red-950/40 text-red-200" : "mr-6 bg-zinc-900 text-zinc-200"}`}>
                {m.me ? m.text : <Md text={m.text} />}
                {!m.me && m.period && <div className="mt-1 text-[10px] font-mono-caps uppercase tracking-widest text-zinc-500">período: {m.period}</div>}
                {!m.me && m.list?.length > 0 && (
                  <div className="mt-2 space-y-1" data-testid={`fab-list-${i}`}>{m.list.map((it, k) => (
                    <button key={k} type="button" data-testid={`fab-list-${i}-${k}`} onClick={() => go(it)} className="w-full text-left rounded-lg border border-zinc-800 px-3 py-2 hover:border-green-brand">
                      <div className="text-sm font-bold text-zinc-100">{it.label}</div><div className="text-[11px] text-zinc-500">{it.sub}</div>
                    </button>
                  ))}</div>
                )}
                {!m.me && m.goto && (
                  <button type="button" data-testid={`assistant-goto-${i}`} onClick={() => { setOpen(false); navigate(m.goto.path); }}
                          className="mt-2 inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-green-brand/15 border border-green-brand/40 text-green-200 text-xs font-bold uppercase hover:bg-green-brand/25">
                    Me leva lá: {m.goto.label} <ArrowRight className="h-3.5 w-3.5" />
                  </button>
                )}
                {!m.me && mode === "help" && m.found === false && (
                  <button type="button" data-testid={`assistant-ticket-${i}`} onClick={() => openTicket(m.support_prefill)}
                          className="mt-2 inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-amber-500/20 border border-amber-500/60 text-amber-200 text-xs font-bold uppercase hover:bg-amber-500/30">
                    <LifeBuoy className="h-3.5 w-3.5" /> Abrir chamado de suporte
                  </button>
                )}
              </div>
            ))}
            {busy && <div className="flex items-center gap-2 text-zinc-400 text-xs"><Loader2 className="h-4 w-4 animate-spin" /> pensando…</div>}
          </div>

          <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="flex items-center gap-2">
            <input data-testid="fab-input" value={q} onChange={(e) => setQ(e.target.value)}
                   placeholder={listening ? "Ouvindo… fale agora" : transcribing ? "Transcrevendo…" : mode === "help" ? "Como lanço uma peça na OS?" : "Pergunte algo…"}
                   className="flex-1 min-w-0 h-10 rounded-md bg-zinc-900 border border-zinc-800 px-3 text-sm text-zinc-100" />
            <Button type="button" data-testid="fab-mic" onClick={toggleMic} disabled={busy || transcribing} title={listening ? "Parar" : "Falar"} aria-label="Falar"
                    className={`h-10 ${listening ? "bg-red-600 hover:bg-red-500 animate-pulse" : "bg-zinc-800 hover:bg-zinc-700"} text-white`}>
              {transcribing ? <Loader2 className="h-4 w-4 animate-spin" /> : listening ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
            </Button>
            <Button data-testid="fab-send" disabled={busy || !q.trim() || overLimit}
                    className="bg-amber-500 hover:bg-amber-400 text-black font-bold">
              <Send className="h-4 w-4" />
            </Button>
          </form>

          {usage && !usage.needs_shop && (
            <div data-testid="assistant-usage" className={`text-[10px] font-mono-caps uppercase tracking-widest ${overLimit ? "text-red-300" : nearLimit ? "text-amber-300" : "text-zinc-500"}`}>
              uso: {usage.used}/{usage.limit}
              {overLimit && " · limite do mês atingido. Fale com o suporte para aumentar."}
              {nearLimit && !overLimit && " · perto do limite do mês."}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
